import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {Space} from 'antd';
import {StyledBudgetEditor, StyledBudgetEditorContainer} from './StyledBudgetEditor';
import BudgetSelector from '../BudgetSelector';
import BudgetPreview from '../BudgetPreview';
import SpinSmall from '../../../SpinSmall';
import {alertActions} from '../../../../state/actions/alert.actions';
import {budgetActions} from '../../../../state/actions/budget.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';

const BudgetEditor = ({
  invoice,
  onUpdate,
  ...rest
}) => {
  const [t] = useTranslation(['main', 'budgets']);
  const isDefinedBudget = useMemo(() => Boolean(invoice?.budget_request_id), [invoice]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    const invoiceId = invoice?.id;
    if (!invoiceId) return;

    const budgetRequestId = invoice?.budget_request_id;
    if (budgetRequestId) {
      dispatch(budgetActions.getBudgetRequest({
        id: budgetRequestId,
        successCallback: setSelectedBudget,
        errorCallback: () => setSelectedBudget(null),
      }))
    }
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOnSave = (budgetId) => {
    setLoading(true);

    dispatch(invoicesActions.assignBudgetToInvoice({
      id: invoice.id,
      data: {budget_request_id: budgetId},
      successCallback: (invoice) => {
        onUpdate?.(invoice);
        setLoading(false);
      },
      errorCallback: () => {
        setLoading(false);
        alertActions.error(t('budgets:alerts.assignBudgetToInvoiceError'));
      }
    }))
  }

  const handleUnlink = () => {
    setLoading(true);

    dispatch(invoicesActions.cancelPaymentRequest({
      id: invoice.id,
      successCallback: (invoice) => {
        onUpdate?.(invoice);
        setLoading(false);
      },
      errorCallback: () => {
        setLoading(false);
        alertActions.error(t('budgets:alerts.unassignBudgetFromInvoiceError'));
      }
    }));
  }

  return (
    <StyledBudgetEditor size='middle' direction='vertical' {...rest}>
      <SpinSmall spinning={loading}>
        <StyledBudgetEditorContainer>
          {isDefinedBudget ? (
            <Space direction='vertical' size={18}>
              {t('Budget')}
              <BudgetPreview budget={selectedBudget} invoice={invoice} onUnlink={handleUnlink} />
            </Space>
          ) : (
            <BudgetSelector invoice={invoice} onSave={handleOnSave} />
          )}
        </StyledBudgetEditorContainer>
      </SpinSmall>
    </StyledBudgetEditor>
  );
};

BudgetEditor.propTypes = {
  invoice: PropTypes.shape({
    budget_request_id: PropTypes.string,
    id: PropTypes.string,
  }),
  onUpdate: PropTypes.func,
};

export default BudgetEditor;
